import React from 'react';
import {observer} from 'mobx-react-lite';
import {splitEventHandler} from '@netx/core-next/utils/fn';
import {getPrefixedLogger} from '@netx/core-next/instance/logger';
import type {DialogProps} from '@netx/core-components/Dialog';
import {ModalManagerStore} from './modalManagerStore';

const logger = getPrefixedLogger('modalManager');

export interface ModalWrapperProps
	extends Required<Pick<DialogProps, 'onClose'>> {
	open: boolean;
	DialogProps?: Partial<DialogProps>;
}

export type ModalWrapperComponentProps<C extends React.ElementType> =
	JSX.IntrinsicAttributes &
		JSX.LibraryManagedAttributes<C, React.ComponentProps<C>>;

export type ModalWrapperPropsPrivate<C extends React.ElementType> = {
	Component: C;
	modalManagerStore: ModalManagerStore;
	modalId: string;
} & ModalWrapperComponentProps<C>;

export const ModalWrapper = observer(
	<C extends React.ElementType>({
		modalId,
		modalManagerStore,
		open,
		onClose,
		Component,
		DialogProps,
		...props
	}: ModalWrapperProps & ModalWrapperPropsPrivate<C>) => {
		return (
			<Component
				{...(props as ModalWrapperComponentProps<C>)}
				open={open}
				onClose={splitEventHandler(onClose, () => {
					modalManagerStore.hide(modalId).catch((ex: any) => {
						logger.error('hide error >', ex);
					});
				})}
				DialogProps={{
					...DialogProps,
					open,
					TransitionProps: {
						...DialogProps?.TransitionProps,
						onExited: splitEventHandler(
							DialogProps?.TransitionProps?.onExited,
							() => {
								logger.debug('exited', modalId);
								modalManagerStore.resolveHidden(modalId);
							},
						),
						onEntered: splitEventHandler(
							DialogProps?.TransitionProps?.onEntered,
							() => {
								logger.debug('entered', modalId);
								modalManagerStore.resolveShown(modalId);
							},
						),
					},
				}}
			/>
		);
	},
);

export default ModalWrapper;
